import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

export interface PlatformSessionRequest {
  id: string;
  churchId: string;
  churchName?: string;
  ticketId: string | null;
  reason: string;
  status: 'pending_platform' | 'pending_church' | 'approved' | 'active';
  impersonationId: string | null;
}

export const usePlatformSessionRequests = (filter: {
  churchId?: string;
  ticketId?: string;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: ['platform-support-session-requests', 'context', filter.churchId ?? null, filter.ticketId ?? null],
    queryFn: () =>
      api.get<{ data: PlatformSessionRequest[]; page: OffsetPage }>('/platform/support/session-requests', {
        query: {
          page: 1,
          pageSize: 5,
          churchId: filter.churchId,
          ticketId: filter.ticketId,
        },
      }),
    enabled: filter.enabled !== false && Boolean(filter.churchId || filter.ticketId),
    refetchInterval: 10_000,
  });

export const PlatformSessionRequestNotice = ({ request }: { request: PlatformSessionRequest }) => {
  const { t } = useTranslation('admin');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const openSession = useMutation({
    mutationFn: () =>
      api.post<{ consoleUrl: string }>(`/platform/support/session-requests/${request.id}/open`, {}),
    onSuccess: async (result) => {
      window.open(result.consoleUrl, '_blank', 'noopener');
      await queryClient.invalidateQueries({ queryKey: ['platform-support-session-requests'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const canOpen = request.status === 'approved' || request.status === 'active';

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-sunken p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-body-sm font-medium text-fg">{t('supportAccess.noticeTitle')}</p>
          <Badge variant={canOpen ? 'success' : 'warning'}>
            {t(`supportAccess.requestStatuses.${request.status}`)}
          </Badge>
        </div>
        <p className="mt-1 text-body-sm text-fg-secondary">{request.reason}</p>
      </div>
      {canOpen ? (
        <Button className="shrink-0 self-end sm:self-auto" loading={openSession.isPending} onClick={() => openSession.mutate()}>
          {t('supportAccess.open')}
        </Button>
      ) : (
        <p className="text-body-sm text-fg-secondary sm:text-right">{t('supportAccess.waitingForChurch')}</p>
      )}
    </div>
  );
};
